import React, { useState, useEffect } from 'react';
import { Modal } from '@shared/components/ui/Modal';
import { Button } from '@shared/components/ui/Button';
import { FileText, Download, AlertCircle, CheckCircle, XCircle, Loader } from 'lucide-react';
import { useSkillsTracker } from '../contexts/SkillsTrackerContext';
import { toast } from '@shared/components/ui/Toaster';

interface UserReport {
  id: string;
  title: string;
  created_at: string;
  status: string;
}

interface ImportSkillsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ImportSkillsModal: React.FC<ImportSkillsModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { fetchUserReports, importSkillsFromReport } = useSkillsTracker();
  const [reports, setReports] = useState<UserReport[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      loadReports();
    }
  }, [isOpen]);

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const userReports = await fetchUserReports();
      setReports(userReports);

      if (userReports.length > 0) {
        setSelectedReportId(userReports[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!selectedReportId) {
      toast.error('Please select a report to import from');
      return;
    }

    try {
      console.log('[ImportModal] Starting import for report:', selectedReportId);
      setImporting(true);
      setError(null);
      setImportResult(null);

      const result = await importSkillsFromReport(selectedReportId);
      console.log('[ImportModal] Import completed:', result);
      setImportResult(result);

      if (result.success) {
        const skillsCount = result.skills_added_to_tracker || 0;
        const message = skillsCount > 0
          ? `Successfully imported ${skillsCount} skill${skillsCount !== 1 ? 's' : ''}!`
          : 'Import completed but no new skills were added';

        toast.success(message);
        onSuccess();
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        const errorMessage = result.errors && result.errors.length > 0
          ? result.errors[0]
          : 'Import completed with errors';
        toast.error(errorMessage);
        setError(errorMessage);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to import skills. Please try again.';
      console.error('[ImportModal] Error importing skills:', {
        message: err.message,
        stack: err.stack,
        error: err
      });
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setImportResult(null);
    setError(null);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Skills from Report">
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : error && !reports.length ? (
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-gray-700 mb-2">Unable to load reports</p>
            <p className="text-sm text-gray-500">{error}</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-700 mb-2">No reports available</p>
            <p className="text-sm text-gray-500">
              Generate a career intelligence report first to import skills
            </p>
          </div>
        ) : (
          <>
            {!importResult ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Report
                  </label>
                  <select
                    value={selectedReportId}
                    onChange={(e) => setSelectedReportId(e.target.value)}
                    disabled={importing}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {reports.map((report) => (
                      <option key={report.id} value={report.id}>
                        {report.title} - {formatDate(report.created_at)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Download className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">What will be imported?</p>
                      <ul className="space-y-1 text-blue-700">
                        <li>Skills from your report analysis</li>
                        <li>Priority rankings and proficiency levels</li>
                        <li>Development milestones and action plans</li>
                        <li>Timeline estimates for skill development</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <div className="text-sm text-red-800">
                        <p className="font-medium">Import Error</p>
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={importing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={importing || !selectedReportId}
                    className="flex items-center gap-2"
                  >
                    {importing ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Import Skills
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className={`border rounded-lg p-4 ${
                  importResult.success ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex gap-3 mb-4">
                    {importResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                    )}
                    <div>
                      <h3 className={`font-medium ${
                        importResult.success ? 'text-green-900' : 'text-yellow-900'
                      }`}>
                        {importResult.success ? 'Import Successful' : 'Import Completed with Issues'}
                      </h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded p-3">
                      <p className="text-2xl font-bold text-gray-900">
                        {importResult.skills_added_to_tracker || 0}
                      </p>
                      <p className="text-sm text-gray-600">Skills Added</p>
                    </div>
                    <div className="bg-white rounded p-3">
                      <p className="text-2xl font-bold text-gray-900">
                        {importResult.milestones_created || 0}
                      </p>
                      <p className="text-sm text-gray-600">Milestones Created</p>
                    </div>
                  </div>

                  {importResult.skills_extracted !== undefined && (
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">{importResult.skills_extracted}</span> skill{importResult.skills_extracted !== 1 ? 's' : ''} found in report,{' '}
                        <span className="font-medium">{importResult.skills_added_to_tracker || 0}</span> added to your tracker
                      </p>
                    </div>
                  )}

                  {importResult.warnings && importResult.warnings.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Warnings:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {importResult.warnings.map((warning: string, idx: number) => (
                          <li key={idx} className="flex gap-2">
                            <span>•</span>
                            <span>{warning}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResult.errors && importResult.errors.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-red-700 mb-2">Errors:</p>
                      <ul className="text-sm text-red-600 space-y-1">
                        {importResult.errors.map((error: string, idx: number) => (
                          <li key={idx} className="flex gap-2">
                            <span>•</span>
                            <span>{error}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleClose}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
